/**
 * Schémas pour le module Immersion (Vague F).
 *
 * Mise en relation pré-recrutement bidirectionnelle entre candidat et
 * employeur. 3 formats : observation / team_meeting / hands_on. Le format
 * "hands_on" déclenche un avertissement légal supplémentaire (contrat
 * obligatoire, rémunération obligatoire).
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
// ─── Référentiels ────────────────────────────────────────────────────────
export const IMMERSION_TYPES = ['observation', 'team_meeting', 'hands_on'];
export const IMMERSION_TYPE_LABELS = {
    observation: 'Observation',
    team_meeting: "Visite & rencontre d'équipe",
    hands_on: 'Mise en pratique',
};
export const IMMERSION_TYPE_DESCRIPTIONS = {
    observation: "Le candidat vient observer un service ou une journée type, sans réaliser de tâches productives. Cadre léger : pas de contrat ni de paiement requis (≤ 2h recommandé).",
    team_meeting: "Visite des locaux, rencontre informelle avec l'équipe, discussion autour d'un café. Sans tâche productive. Pas de contrat requis.",
    hands_on: "Mise en pratique réelle (préparation, service, accueil clients). C'est du travail au sens du droit français : contrat de travail, déclaration URSSAF et rémunération au moins au SMIC sont OBLIGATOIRES.",
};
export const IMMERSION_DURATIONS = [
    'h1',
    'h2',
    'half_day',
    'one_service',
    'full_day',
];
export const IMMERSION_DURATION_LABELS = {
    h1: '1 heure',
    h2: '2 heures',
    half_day: 'Demi-journée',
    one_service: 'Un service',
    full_day: 'Journée complète',
};
export const IMMERSION_STATUSES = [
    'requested',
    'accepted',
    'declined',
    'scheduled',
    'completed',
    'no_show',
    'cancelled',
];
export const IMMERSION_STATUS_LABELS = {
    requested: 'Demande envoyée',
    accepted: 'Acceptée — chartes à signer',
    declined: 'Déclinée',
    scheduled: 'Planifiée',
    completed: 'Réalisée',
    no_show: "L'une des parties n'est pas venue",
    cancelled: 'Annulée',
};
export const IMMERSION_OUTCOMES = [
    'mutual_yes',
    'mutual_no',
    'candidate_only_yes',
    'employer_only_yes',
    'no_show',
];
// ─── Création — schéma commun (peu importe l'initiateur) ─────────────────
const immersionCreateBase = {
    type: z.enum(IMMERSION_TYPES),
    duration: z.enum(IMMERSION_DURATIONS),
    // 1 à 3 créneaux proposés. Tous dans le futur.
    proposedDates: z
        .array(z.coerce.date())
        .min(1, 'Proposez au moins une date.')
        .max(3, 'Maximum 3 créneaux proposés.')
        .refine((dates) => dates.every((d) => d > new Date()), 'Tous les créneaux doivent être dans le futur.'),
    initialMessage: z.string().trim().max(500).optional().or(z.literal('')),
};
/** Demande candidat → entreprise. La cible est l'annonce (jobOfferId). */
export const immersionCreateByCandidateSchema = z.object({
    ...immersionCreateBase,
    jobOfferId: uuidSchema,
});
/** Demande entreprise → candidat. La cible est le candidat (candidateId). */
export const immersionCreateByCompanySchema = z.object({
    ...immersionCreateBase,
    candidateId: uuidSchema,
    // Optionnel : si l'employeur veut associer la demande à une de ses annonces.
    jobOfferId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
});
// ─── Réponse à une demande ───────────────────────────────────────────────
/** Le destinataire accepte un créneau (et accepte la charte). */
export const immersionAcceptSchema = z.object({
    scheduledAt: z.coerce.date(),
    charterAccepted: z.literal(true, {
        errorMap: () => ({ message: "Vous devez accepter la charte d'immersion." }),
    }),
    message: z.string().trim().max(500).optional().or(z.literal('')),
});
/** Le destinataire décline. */
export const immersionDeclineSchema = z.object({
    message: z.string().trim().max(500).optional().or(z.literal('')),
});
/** L'initiateur accepte la charte de son côté pour finaliser la planification. */
export const immersionConfirmCharterSchema = z.object({
    charterAccepted: z.literal(true, {
        errorMap: () => ({ message: "Vous devez accepter la charte d'immersion." }),
    }),
});
// ─── Évaluation post-immersion ───────────────────────────────────────────
const ratingScore = z.number().int().min(1).max(5);
/** Évaluation par le candidat : il note l'expérience. */
export const immersionRateByCandidateSchema = z.object({
    ambiance: ratingScore,
    accueil: ratingScore,
    coherenceAvecAnnonce: ratingScore,
    intention: z.enum(['yes', 'no', 'maybe']),
    note: z.string().trim().max(500).optional().or(z.literal('')),
});
/** Évaluation par l'employeur : il note le candidat. */
export const immersionRateByCompanySchema = z.object({
    motivation: ratingScore,
    savoirEtre: ratingScore,
    fitCulturel: ratingScore,
    intention: z.enum(['yes', 'no', 'maybe']),
    note: z.string().trim().max(500).optional().or(z.literal('')),
});
// ─── Charte d'immersion (versionnée) ─────────────────────────────────────
/**
 * Texte canonique de la charte d'immersion, version 1.0.
 *
 * Affiché aux deux parties au moment de l'acceptation. Servir comme
 * source unique de vérité côté UI (pas de duplication front/back).
 */
export const IMMERSION_CHARTER_VERSION = '1.0';
export const IMMERSION_CHARTER_TEXT = {
    version: IMMERSION_CHARTER_VERSION,
    intro: "Une immersion n'est pas un travail dissimulé. Voici les règles qui protègent les deux parties selon le format choisi.",
    rules: {
        observation: [
            "Pas de tâche productive : le candidat ne réalise aucune action de service, de préparation ou d'accueil.",
            "Durée recommandée ≤ 2 heures — au-delà, choisir un autre format.",
            "Pas de contrat de travail nécessaire. Pas de rémunération obligatoire.",
            "L'employeur est tenu d'assurer la sécurité physique du candidat dans ses locaux (RC pro de l'établissement).",
        ],
        team_meeting: [
            "Visite des locaux + entretien informel avec l'équipe. Sans tâche productive.",
            "Pas de contrat de travail nécessaire. Pas de rémunération obligatoire.",
            "L'employeur reste responsable de la sécurité du candidat dans ses locaux.",
        ],
        hands_on: [
            "Mise en pratique = travail effectif. Le droit français impose : ",
            "• Un contrat écrit (CDD d'usage / contrat extra / convention de stage si en formation), signé AVANT le début.",
            "• Une rémunération au moins égale au SMIC horaire (12,12 € brut au 01/2026).",
            "• Une déclaration préalable à l'embauche (DPAE) à l'URSSAF.",
            "• La couverture par la RC pro de l'établissement.",
            "• Pour un CDD ≥ 14 jours : visite médicale d'embauche obligatoire.",
            "Le travail dissimulé est passible de 3 ans d'emprisonnement et 45 000 € d'amende.",
        ],
    },
    comptoirRole: [
        "Le Comptoir est un facilitateur de mise en relation, pas un employeur.",
        "Aucun contrat de travail, aucun paiement et aucune assurance ne transitent par notre plateforme.",
        "Les deux parties restent seules responsables du respect du droit du travail.",
        "En cas de litige : support@stelvyo.com. Médiation possible. Avertissement public en cas d'abus avéré.",
    ],
    freedom: "Vous restez libres de ne pas vous engager après l'immersion, sans justification, des deux côtés.",
};
//# sourceMappingURL=immersion.js.map